import { Box, ButtonBase, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { RadioCardCotizadorOption } from "./RadioCardGroupCotizador";

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: RadioCardCotizadorOption[];
}

/**
 * Replica `.mc-equipos .radio-card` del cotizador de Mercader: muchas opciones cortas en
 * cuadrícula, con la tarjeta apilada (ícono → texto) para que el cuerpo no compita por ancho con
 * el ícono y el check. Cuatro por fila cuando hay ancho y dos cuando no (`minmax(150px, 1fr)`).
 */
export const RadioCardGridCotizador = ({ value, onChange, options }: Props) => (
  <Box
    role="radiogroup"
    sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}
  >
    {options.map((opt) => {
      const on = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={on}
          onClick={() => onChange(opt.value)}
          data-testid={opt.testId}
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            textAlign: "left",
            gap: "9px",
            p: "13px 14px 14px",
            border: "1.5px solid",
            borderColor: on ? "primary.accent" : "divider",
            borderRadius: "8px",
            bgcolor: on ? "primary.base" : "background.paper",
            boxShadow: (theme) => (on ? `0 0 0 1px ${theme.palette.primary.accent} inset` : "none"),
            transition: "border-color .18s ease, background-color .18s ease",
            "&:hover": { borderColor: "primary.accent", bgcolor: "primary.base" },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: on ? "primary.accent" : "tokens.surface2",
              color: on ? "#fff" : "tokens.textTertiary",
              border: "1px solid",
              borderColor: on ? "primary.accent" : "divider",
              transition: "background-color .2s ease, color .2s ease",
            }}
          >
            {opt.icon}
          </Box>
          <Box sx={{ width: "100%", pr: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: "primary.dark" }}>
              {opt.label}
            </Typography>
            <Typography
              variant="body3"
              color="tokens.textTertiary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
            >
              {opt.sub}
            </Typography>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 11,
              right: 11,
              width: 18,
              height: 18,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: on ? "primary.accent" : "tokens.surface2",
              border: "1.5px solid",
              borderColor: on ? "primary.accent" : "#D5DCE4",
              color: on ? "#fff" : "transparent",
            }}
          >
            <Check sx={{ fontSize: 12 }} />
          </Box>
        </ButtonBase>
      );
    })}
  </Box>
);
